import { Fragment } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { cn } from '../../utils/cn';
import { isNavItemActive, sectionHasActiveItem } from './navItems';

/** `dot` — ellipse 5×5; activo $sage-50, default $sage-border. */
function NavDot({ active }) {
  return (
    <span
      className={cn(
        // eslint-disable-next-line no-restricted-syntax -- ponto de 5px do .pen, sem token
        'h-[5px] w-[5px] shrink-0 rounded-ds-full',
        active ? 'bg-sage-50' : 'bg-sage-border',
      )}
      aria-hidden
    />
  );
}

/** `badge` do header — 34×34, $radius-ds-sm; activo gradiente sage + glow. */
function SectionBadge({ icon: Icon, active }) {
  return (
    <span
      className={cn(
        // eslint-disable-next-line no-restricted-syntax -- badge 34×34 do .pen, sem token
        'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-ds-sm',
        active
          ? // eslint-disable-next-line no-restricted-syntax -- sombra do .pen (offset/blur) com token de cor
            'bg-gradient-to-br from-sage-500 to-sage-600 text-sage-50 shadow-[0_4px_12px] shadow-sage-glow'
          : 'border border-sage-border bg-sage-tint text-sage-on-tint',
      )}
    >
      {/* eslint-disable-next-line no-restricted-syntax -- ícone 18×18 do .pen, sem token */}
      <Icon className="h-[18px] w-[18px]" aria-hidden />
    </span>
  );
}

/**
 * `ds/nav/sidebar-section--header` / `--header-active`: gap $space-2,
 * padding [$space-2, $space-2, 0, $space-2], radius $radius-ds-sm.
 */
function SectionHeader({ section, active, onNavigate }) {
  const content = (
    <>
      <SectionBadge icon={section.icon} active={active} />
      <span
        className={cn(
          'min-w-0 flex-1 truncate font-display text-type-title-card font-semibold',
          active ? 'text-sage-on-tint' : 'text-foreground',
        )}
      >
        {section.label}
      </span>
    </>
  );

  const rowClass = cn(
    'flex items-center gap-space-2 rounded-ds-sm px-space-2 pb-0 pt-space-2 transition-colors',
    active ? 'bg-sage-tint' : 'hover:bg-muted',
  );

  if (section.to) {
    return (
      <NavLink
        to={section.to}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={rowClass}
      >
        {content}
      </NavLink>
    );
  }

  return <div className={rowClass}>{content}</div>;
}

/**
 * `ds/nav/sidebar-child--default` / `--active`: gap $space-2-5,
 * padding [$space-2, $space-3], radius $radius-ds-md; activo $sage-tint + glow.
 */
function SectionChildLink({ item, onNavigate }) {
  const location = useLocation();
  const active = isNavItemActive(location.pathname, item);

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-space-2-5 rounded-ds-md px-space-3 py-space-2 text-type-body-sm transition-colors',
        active
          ? // eslint-disable-next-line no-restricted-syntax -- sombra do .pen (offset/blur) com token de cor
            'bg-sage-tint font-semibold text-sage-on-tint shadow-[0_8px_20px] shadow-sage-glow'
          : 'font-normal text-muted-foreground hover:bg-muted',
      )}
    >
      <NavDot active={active} />
      <span className="min-w-0 truncate">{item.label}</span>
    </NavLink>
  );
}

/**
 * Secção de navegação (badge + label + filhos indentados a $space-10) —
 * partilhada pela sidebar desktop (`ds/nav/sidebar--desktop`) e pelo drawer de
 * menu (`ds/overlay/side-drawer · nav`). As duas divergem só na densidade:
 * a sidebar usa 2px entre linhas, o drawer usa $space-1 entre header e itens.
 */
export function NavSection({ section, onNavigate, variant = 'sidebar' }) {
  const location = useLocation();
  const hasActive = sectionHasActiveItem(location.pathname, section);
  const items = section.items || [];
  const isDrawer = variant === 'drawer';

  return (
    <div
      className={cn(
        'flex flex-col py-space-1',
        isDrawer ? 'gap-space-1' : 'gap-0.5',
      )}
    >
      <SectionHeader section={section} active={hasActive} onNavigate={onNavigate} />
      {items.length > 0 ? (
        <ul className={cn('flex flex-col pl-space-10', !isDrawer && 'gap-0.5')}>
          {items.map((item) => (
            <li key={item.to}>
              <SectionChildLink item={item} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Lista de secções com `rule` (1px $border) entre cada uma — o contentor `nav`
 * usa $space-2 na sidebar e $space-1 no drawer.
 */
export function NavSectionList({ sections, onNavigate, variant = 'sidebar', className }) {
  return (
    <div
      className={cn(
        'flex flex-col',
        variant === 'drawer' ? 'gap-space-1' : 'gap-space-2',
        className,
      )}
    >
      {sections.map((section, index) => (
        <Fragment key={section.id}>
          {index > 0 ? <div className="h-px shrink-0 bg-border" aria-hidden /> : null}
          <NavSection section={section} onNavigate={onNavigate} variant={variant} />
        </Fragment>
      ))}
    </div>
  );
}
